// Cumulative revenue maths for the Umsatzverlauf chart. `today` is a parameter rather than a
// `new Date()` inside, so every figure is deterministic in a test.

import { eurAmount } from './invoiceCurrency';

// The same set the overview's KPIs and the Monatsumsatz bars use, so the figures agree.
export const REVENUE_STATUSES = new Set(['sent', 'paid']);

export const MONTHS_IN_YEAR = 12;

// A month's invoicing rarely lands evenly, so a hair under the line is not yet a warning.
const ON_TRACK_RATIO = 0.9;

// On 2 January one €20.000 invoice extrapolates to €3.650.000, which then sets the
// chart's axis and flattens the real curve onto the baseline.
const MIN_PROJECTION_ELAPSED = 1 / 12;

/** The 0-based month of an `issue_date` in `year`. Parsed off the string: `new Date(...)` is
 *  UTC midnight, so `.getMonth()` answers the previous month west of UTC. */
export function issueMonth(issueDate, year) {
    if (typeof issueDate !== 'string') return null;
    const m = /^(\d{4})-(\d{2})/.exec(issueDate);
    if (!m || Number(m[1]) !== Number(year)) return null;
    const month = Number(m[2]) - 1;
    return month >= 0 && month < MONTHS_IN_YEAR ? month : null;
}

/** Net revenue in EUR per calendar month of `year`, index 0 = January. */
export function monthlyNet(invoices, year) {
    const months = Array(MONTHS_IN_YEAR).fill(0);
    for (const inv of invoices || []) {
        if (!REVENUE_STATUSES.has(inv?.status)) continue;
        const month = issueMonth(inv?.issue_date, year);
        if (month === null) continue;
        months[month] += eurAmount(inv.total_net, inv);
    }
    return months;
}

/** Net EUR booked in `year` up to `cutoffIso`. The monthly series cannot express it: on 3
 *  September its current-month entry would measure three days against the whole of last. */
export function netUpTo(invoices, year, cutoffIso) {
    let sum = 0;
    for (const inv of invoices || []) {
        if (!REVENUE_STATUSES.has(inv?.status)) continue;
        const d = inv?.issue_date;
        if (typeof d !== 'string' || issueMonth(d, year) === null) continue;
        if (d.slice(0, 10) <= cutoffIso) sum += eurAmount(inv.total_net, inv);
    }
    return sum;
}

/** Running totals of a monthly series, same length. */
export function runningTotal(monthly) {
    let sum = 0;
    return monthly.map(v => (sum += v));
}

/** True for a year that has 366 days. */
export function isLeapYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/** How far the curve is drawn. A future year runs to December too: an invoice may be dated
 *  ahead, and the chart must not read € 0,00 under a KPI showing that year's real total. */
export function lastActualMonth(year, today) {
    const thisYear = today.getFullYear();
    if (year === thisYear) return today.getMonth();
    return MONTHS_IN_YEAR - 1;
}

/** How much of `year` has elapsed, 0..1, by day: mid-September is 72 %, not 9/12. */
export function elapsedFraction(year, today) {
    const thisYear = today.getFullYear();
    if (year < thisYear) return 1;
    if (year > thisYear) return 0;
    const start = Date.UTC(thisYear, 0, 1);
    // +1 so 1 January is one day, never zero.
    const dayOfYear = Math.floor((Date.UTC(thisYear, today.getMonth(), today.getDate()) - start) / 86400000) + 1;
    return dayOfYear / (isLeapYear(year) ? 366 : 365);
}

/** Whether the previous year can be trusted: `GET /Invoice` caps after `ORDER BY IssueDate
 *  DESC`, so the oldest rows are the missing ones and a partial year reads as a collapse. */
export function prevYearComplete(invoices, year, truncated) {
    if (!truncated) return true;
    let oldest = null;
    for (const inv of invoices || []) {
        const d = inv?.issue_date;
        if (typeof d === 'string' && (oldest === null || d < oldest)) oldest = d;
    }
    // `<=`: an oldest row of exactly 1 January means the whole year arrived.
    return oldest !== null && oldest <= `${year - 1}-01-01`;
}

/** Everything the chart draws. A null `target` nulls every derived figure, so the chart omits
 *  that layer instead of drawing a goal of zero. */
export function revenueProgress({ invoices = [], year, target = null, today, truncated = false }) {
    const now = today || new Date();
    const lastMonth = lastActualMonth(year, now);
    const elapsed = elapsedFraction(year, now);

    const monthly     = monthlyNet(invoices, year);
    const monthlyPrev = monthlyNet(invoices, year - 1);
    const cumulative     = runningTotal(monthly);
    const cumulativePrev = runningTotal(monthlyPrev);

    // null, not 0: a zero-padded cumulative curve runs flat to December and reads as
    // "revenue stopped in September".
    const series = cumulative.map((v, m) => (m <= lastMonth ? v : null));

    const ytd = lastMonth >= 0 ? cumulative[lastMonth] : 0;

    // Cut at the same day, not the same month or the full year: either of those reports a
    // decline for an unchanged business. Only the running year needs the cut.
    const isRunningYear = year === now.getFullYear();
    const prevCutoff = isRunningYear
        ? `${year - 1}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
        : `${year - 1}-12-31`;
    const prevSamePeriod = netUpTo(invoices, year - 1, prevCutoff);
    const prevFullYear   = cumulativePrev[MONTHS_IN_YEAR - 1];
    const prevDelta      = ytd - prevSamePeriod;
    // A share of zero has no percentage; null lets the UI say so.
    const prevDeltaPct   = prevSamePeriod > 0 ? prevDelta / prevSamePeriod : null;

    const hasTarget  = typeof target === 'number' && isFinite(target) && target > 0;
    const attainment = hasTarget ? ytd / target : null;
    // Soll bis heute: the target spread evenly across the year.
    const paceTarget = hasTarget ? target * elapsed : null;
    const paceDelta  = hasTarget ? ytd - paceTarget : null;
    // Hochrechnung: the run rate carried to 31 December. Not gated on a target — a
    // projection needs no goal — but gated on MIN_PROJECTION_ELAPSED.
    const projection =
        elapsed >= MIN_PROJECTION_ELAPSED && ytd > 0 ? ytd / elapsed : null;

    let pace = null;
    if (hasTarget) {
        // Nothing owed yet is 'none', not 'ahead': an untouched future year is not
        // beating its plan.
        if (paceTarget <= 0) pace = 'none';
        else if (ytd >= paceTarget) pace = 'ahead';
        else if (ytd >= paceTarget * ON_TRACK_RATIO) pace = 'ontrack';
        else pace = 'behind';
    }

    return {
        year,
        lastMonth,
        elapsed,
        monthly,
        monthlyPrev,
        series,
        cumulative,
        cumulativePrev,
        ytd,
        prevSamePeriod,
        prevCutoff,
        prevFullYear,
        prevDelta,
        prevDeltaPct,
        prevYearComplete: prevYearComplete(invoices, year, truncated),
        hasPrevYear: prevFullYear > 0 && prevYearComplete(invoices, year, truncated),
        target: hasTarget ? target : null,
        attainment,
        paceTarget,
        paceDelta,
        projection,
        pace,
    };
}
